import { useState } from 'react'
import { Minus, Plus, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { DEFAULT_MAX_PARTY_SIZE } from '../lib/partySize'
import { showToast } from './Toast'
import { api, ApiError } from '../services/api'
import StripeCheckout from './StripeCheckout'
import type { Reservation } from '../store/useStore'
import {
  isDepositRefundable,
  prepaidRefundAmountByHours,
} from '../pages/StoreDetail/reservationPolicy'

interface Props {
  open: boolean
  onClose: () => void
  reservation: Reservation
  storeName: string
  /** course の per-person 価格 (= UI で差額 preview する用)。未指定なら preview しない。 */
  pricePerPerson?: number
  /** course の partySizeRange (= clamp する用)。未指定なら 1〜既定上限 (REQ-28)。 */
  minPartySize?: number
  maxPartySize?: number
}

type IncrementalPI = {
  clientSecret: string
  paymentIntentId: string
  amount: number
  captureMethod: 'automatic' | 'manual'
  connectedAccountId: string
}

type Step = 'edit' | 'payment'

/**
 * #745 Phase 2.1: 予約人数を変更するモーダル.
 *
 * フロー:
 *  1. edit step — +/- ボタンで人数調整 + 差額 preview + 任意の理由
 *  2. submit
 *     - 減少: 部分返金完了 → toast → close
 *     - 増加 (course 予約): 増分 PI 返却 → payment step に遷移 → Stripe Elements → 決済成功 → close
 *     - 増加 (席のみ): API のみで完結 → toast → close
 */
export default function ChangePartySizeModal({
  open,
  onClose,
  reservation,
  storeName,
  pricePerPerson,
  minPartySize = 1,
  maxPartySize = DEFAULT_MAX_PARTY_SIZE,
}: Props) {
  const { t } = useI18n()
  const [partySize, setPartySize] = useState<number>(reservation.partySize)
  const [reason, setReason] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<Step>('edit')
  const [incrementalPI, setIncrementalPI] = useState<IncrementalPI | null>(null)

  // open 切替で state リセット (derive-state-from-props pattern)
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setPartySize(reservation.partySize)
      setReason('')
      setStep('edit')
      setIncrementalPI(null)
    }
  }

  if (!open) return null

  const delta = partySize - reservation.partySize
  const diffAmount = pricePerPerson ? Math.abs(delta) * pricePerPerson : null

  // 管理番号040: 減らした分は「その人数分のキャンセル」として段階返金率がかかる。
  // サーバの _change_party_size_with_payment と同じ式で、押す前に手取りを出す。
  const hoursUntilReservation =
    (new Date(`${reservation.date}T${reservation.time}:00+09:00`).getTime() - Date.now()) / 3600000
  const estimatedRefund = (() => {
    if (delta >= 0 || !diffAmount) return 0
    if (reservation.paymentTiming === 'deposit') {
      // 手付金は binary (7 日以上前なら全額、それ未満は没収)
      return isDepositRefundable(hoursUntilReservation) ? diffAmount : 0
    }
    return prepaidRefundAmountByHours(diffAmount, hoursUntilReservation)
  })()
  const cancellationFee = diffAmount && delta < 0 ? Math.max(0, diffAmount - estimatedRefund) : 0

  // 課金タイプを 3 種類に分類して preview 文言を出し分ける (= backend の実挙動と整合)
  //   - 'paid': 既に決済確定 → 部分返金 / 追加決済が走る (Stripe 経由)
  //   - 'authorized': onsite + 保証 ON (= manual capture 与信のみ) → 与信額が更新される
  //   - 'unpaid': onsite + 保証 OFF / paymentStatus !== 'paid' → 店舗での会計が変わるだけ、Stripe 操作なし
  const paymentImpact: 'paid' | 'authorized' | 'unpaid' = (() => {
    if (reservation.paymentStatus === 'paid' || reservation.paymentStatus === 'partially_refunded') {
      return 'paid'
    }
    if (
      reservation.paymentTiming === 'onsite' &&
      reservation.captureMethod === 'manual' &&
      reservation.paymentStatus === 'authorized'
    ) {
      return 'authorized'
    }
    return 'unpaid'
  })()

  const dec = () => setPartySize(p => Math.max(minPartySize, p - 1))
  const inc = () => setPartySize(p => Math.min(maxPartySize, p + 1))

  const handleSubmit = async () => {
    if (submitting) return
    if (delta === 0) {
      showToast({ type: 'error', message: t('changeParty.unchanged') })
      return
    }
    setSubmitting(true)
    try {
      const res = await api.changeReservationPartySize(reservation.id, partySize, reason.trim() || undefined)
      // 増加 + course = 増分 PI 返却 → 決済 step
      if (res.incrementalPaymentIntent) {
        setIncrementalPI(res.incrementalPaymentIntent)
        setStep('payment')
        return
      }
      // 減少 or 増加 (席のみ / onsite) = この場で完結
      if (typeof res.refundAmount === 'number' && res.refundAmount > 0) {
        showToast({
          type: 'success',
          message: t('changeParty.refunded', { amount: res.refundAmount.toLocaleString() }),
        })
      } else {
        showToast({ type: 'success', message: t('changeParty.updated') })
      }
      onClose()
    } catch (e) {
      const detail = (e as Error)?.message || ''
      // 管理番号040: 締切超過は構造化 detail (code) で返る。400 の message は
      // apiFetch が汎用文言に差し替えるので、**message の文字列一致では判定できない**。
      const code = (e as ApiError)?.code
      console.error('[ChangePartySize] failed:', e)
      showToast({
        type: 'error',
        message: code === 'change_deadline_passed'
          ? t('changeParty.deadlinePassed')
          : code === 'change_pending'
            ? t('myReservations.changeLockedPending')
            : detail.includes('out of range')
              ? t('changeParty.outOfRange', { min: String(minPartySize), max: String(maxPartySize) })
              : t('changeParty.failed'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  // 増分決済の Stripe Elements step
  if (step === 'payment' && incrementalPI) {
    return (
      <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
        <div className="w-full max-w-sm rounded-2xl bg-white p-5 max-h-[90vh] overflow-y-auto animate-scale-in">
          <h3 className="font-display text-lg font-bold text-text-primary mb-3">
            {t('changeParty.paymentStepTitle')}
          </h3>
          <p className="font-body text-xs text-text-secondary mb-4">
            {t('changeParty.paymentStepHint')}
          </p>
          <StripeCheckout
            clientSecret={incrementalPI.clientSecret}
            amount={incrementalPI.amount}
            storeName={storeName}
            connectedAccountId={incrementalPI.connectedAccountId}
            paymentIntentId={incrementalPI.paymentIntentId}
            storeId={reservation.storeId}
            captureMethod={incrementalPI.captureMethod}
            onSuccess={() => {
              showToast({ type: 'success', message: t('changeParty.paymentComplete') })
              onClose()
            }}
            onCancel={() => {
              showToast({ type: 'info', message: t('changeParty.paymentDeferred') })
              onClose()
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-text-primary">
            {t('changeParty.title')}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
            aria-label="close"
          >
            <X size={16} className="text-text-secondary" />
          </button>
        </div>

        <p className="font-body text-xs text-text-tertiary mb-4">
          {storeName} · {reservation.date} {reservation.time}
        </p>

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('changeParty.newPartySize')}
        </p>
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={dec}
            disabled={partySize <= minPartySize}
            className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center tap-effect disabled:opacity-30"
          >
            <Minus size={18} className="text-text-primary" />
          </button>
          <span className="font-display text-3xl font-bold text-text-primary w-16 text-center tabular-nums">
            {partySize}
          </span>
          <button
            onClick={inc}
            disabled={partySize >= maxPartySize}
            className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center tap-effect disabled:opacity-30"
          >
            <Plus size={18} className="text-text-primary" />
          </button>
        </div>
        <p className="text-center font-body text-xs text-text-tertiary mb-4">
          {t('changeParty.range', { min: String(minPartySize), max: String(maxPartySize) })}
          {' · '}
          {t('changeParty.previous', { count: String(reservation.partySize) })}
        </p>

        {/* 管理番号040: 減少は「その人数分のキャンセル」なので、段階キャンセル料が
         *  かかる。**押す前に手取りを見せる** — 満額returns と思って減らしてから
         *  「思ったより戻らない」となるのが一番揉める。計算はサーバと同じ式
         *  (reservationPolicy.ts はサーバ policy の写し)。 */}
        {delta < 0 && diffAmount !== null && diffAmount > 0 && paymentImpact === 'paid' && (
          <div className="rounded-lg p-3 mb-4 bg-matcha/10 space-y-1">
            <div className="flex items-center justify-between font-body text-xs text-text-secondary">
              <span>{t('changeParty.cancelledPortion')}</span>
              <span className="tabular-nums">&yen;{diffAmount.toLocaleString()}</span>
            </div>
            {cancellationFee > 0 && (
              <div className="flex items-center justify-between font-body text-xs text-text-secondary">
                <span>{t('changeParty.cancellationFee')}</span>
                <span className="tabular-nums">&minus;&yen;{cancellationFee.toLocaleString()}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-matcha/20 pt-1 font-body text-xs font-semibold text-text-primary">
              <span>{t('changeParty.refundEstimate')}</span>
              <span className="tabular-nums">&yen;{estimatedRefund.toLocaleString()}</span>
            </div>
            <p className="font-body text-[11px] text-text-tertiary leading-relaxed pt-1">
              {t('changeParty.refundPolicyNote')}
            </p>
          </div>
        )}

        {/* 差額 / 影響 preview — paymentImpact で 3 通りに出し分ける (= backend 実挙動と整合) */}
        {delta !== 0 && diffAmount !== null && diffAmount > 0 && !(delta < 0 && paymentImpact === 'paid') && (() => {
          let messageKey: string
          let bgClass: string
          if (paymentImpact === 'paid') {
            messageKey = delta > 0 ? 'changeParty.previewIncrease' : 'changeParty.previewDecrease'
            bgClass = delta > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-matcha/10'
          } else if (paymentImpact === 'authorized') {
            messageKey = delta > 0 ? 'changeParty.previewIncreaseAuthorized' : 'changeParty.previewDecreaseAuthorized'
            bgClass = 'bg-amber-50 border border-amber-200'
          } else {
            // unpaid (= onsite + 保証 OFF) — Stripe 操作なし、店舗での会計が変わるだけ
            messageKey = delta > 0 ? 'changeParty.previewIncreaseUnpaid' : 'changeParty.previewDecreaseUnpaid'
            bgClass = 'bg-gray-50 border border-gray-200'
          }
          return (
            <div className={`rounded-lg p-3 mb-4 ${bgClass}`}>
              <p className="font-body text-xs text-text-primary leading-relaxed">
                {t(messageKey, { amount: diffAmount.toLocaleString() })}
              </p>
            </div>
          )
        })()}

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('changeParty.reasonLabel')}
        </p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={t('changeParty.reasonPlaceholder')}
          rows={2}
          className="w-full bg-gray-50 rounded-lg px-3 py-2 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30 mb-4 resize-none"
        />

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-gray-200 text-sm font-body font-semibold text-text-secondary"
          >
            {t('changeParty.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || delta === 0}
            className={`flex-1 h-11 rounded-xl text-sm font-body font-semibold transition-colors ${
              submitting || delta === 0 ? 'bg-gray-300 text-white' : 'bg-yokoso-red text-white tap-effect'
            }`}
          >
            {submitting ? '...' : delta > 0 ? t('changeParty.confirmIncrease') : t('changeParty.confirmDecrease')}
          </button>
        </div>
      </div>
    </div>
  )
}
